import CardModuleMulti from "../components/CardModuleMulti.js";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api.js";
import MapModuleForMaps from "../components/MapModuleForMaps.js";
import Button from "@mui/material/Button";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import Typography from "@mui/material/Typography";
import Buttons from "../components/Buttons.js";
import CustomizedDialogs from "../components/DownloadAlert.js";
import CustomizedDialogsInfo from "../components/InfoAlert.js";
import CustomizedDialogsFilter from "../components/FilterAlert.js";
import { usePageMetadata } from "../hooks/usePageMetadata";
import { selectInitialTrip } from "../utils/tripSelection";

// Start building touch events

const API_URL = process.env.REACT_APP_API;

export default function Maps() {
  const [tripId, setTripId] = useState(null);
  const [photoId, setPhotoId] = useState(0);
  const [displayOption, setDisplayOption] = useState("gallery");
  const [openGpxDialog, setOpenGpxDialog] = useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [currentTrip, setCurrentTrip] = useState(null);
  const [mapId, setMapId] = useState(0);
  const [chosenRegion, setChosenRegion] = useState("Dolomites");
  const [trips, setTrips] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false); //for full screen
  const [touchInfo, setTouchInfo] = useState({
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0,
  });
  const [pageSize, setPageSize] = useState(20);
  const [showManyTracks, setShowManyTracks] = useState(false);
  const [highPagination, setHighPagination] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [filterDialogMessage, setFilterDialogMessage] = useState("");
  const [startYear, setStartYear] = useState(2022);
  const [endYear, setEndYear] = useState(2025);
  const skipNextRead = useRef(false);
  const [filterByYear, setFilterByYear] = useState(false);
  const [filterByRegion, setFilterByRegion] = useState(false);

  const { tripId: tripIdParam } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (!tripIdParam || trips.length === 0) return;
    const targetTrip = trips.find((trip) => String(trip.id) === tripIdParam);
    if (targetTrip) {
      setTripId(targetTrip.id);
      setCurrentTrip(targetTrip);
    }
  }, [tripIdParam, trips]);

  useEffect(() => {
    if (!tripId) return;
    if (String(tripId) !== tripIdParam) {
      navigate(`/maps/${tripId}`, { replace: true });
    }
  }, [tripId, tripIdParam, navigate]);

  const metaTitle = currentTrip
    ? `${currentTrip.attributes.cardTitle} | Mountain Biking Trails - ${chosenRegion}`
    : `Mountain Biking Trails - ${chosenRegion}`;
  const metaDescription = currentTrip
    ? `View ${currentTrip.attributes.cardTitle} in ${chosenRegion} with route maps, photos, difficulty, and distance.`
    : `Explore scenic mountain biking routes, maps, and photos from ${chosenRegion}.`;
  usePageMetadata(metaTitle, metaDescription);

  const refPage = useRef(1);

  const ref = useRef(null);

  const divRef = useRef(null); //for full screen
  const handleTap = () => {
    if (!isFullscreen) {
      if (divRef.current.requestFullscreen) {
        divRef.current.requestFullscreen();
      } else if (divRef.current.mozRequestFullScreen) {
        // Firefox
        divRef.current.mozRequestFullScreen();
      } else if (divRef.current.webkitRequestFullscreen) {
        // Chrome, Safari, and Opera
        divRef.current.webkitRequestFullscreen();
      } else if (divRef.current.msRequestFullscreen) {
        // IE/Edge
        divRef.current.msRequestFullscreen();
      }
      setIsFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.mozCancelFullScreen) {
        // Firefox
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        // Chrome, Safari, and Opera
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        // IE/Edge
        document.msExitFullscreen();
      }
      setIsFullscreen(false);
    }
  };

  const initPolygon = {
    polyline: [
      {
        lat: 45.931355925276875,
        lng: 10.892878202721477,
      },
    ],
  };

  // initial load
  useEffect(() => {
    readCards();
    // const polygon = initPolygon;
  }, []);

  useEffect(() => {
    if (tripId === null) return;
    setPhotoId(0);
    setCurrentTrip(trips.find((trip) => trip.id === tripId));
  }, [tripId]);

  useEffect(() => {
    if (displayOption === "map" && tripId !== null) {
      setMapId(tripId);
    }
  }, [displayOption, tripId]);

  useEffect(() => {
    if (skipNextRead.current) {
      skipNextRead.current = false;
      return;
    }
    refPage.current = 1;
    setTrips([]);
    readCards();
    setPhotoId(0);
    setMapId(0);
    setDisplayOption("gallery");
  }, [
    chosenRegion,
    pageSize,
    startYear,
    endYear,
    filterByYear,
    filterByRegion,
  ]);

  useEffect(() => {
    if (openFilterDialog) {
      setFilterDialogMessage("");
    }
  }, [openFilterDialog]);

  useEffect(() => {
    if (highPagination) {
      setPageSize(200);
    } else {
      setPageSize(20);
    }
  }, [highPagination]);

  const fetchTrips = async ({
    region,
    page,
    pageSize,
    filterByYear,
    filterByRegion,
    startYear,
    endYear,
  }) => {
    setIsLoading(true);
    setError(null);

    const filtrationString = filterByRegion
      ? "&filters[trackLink][$eq]=" + region
      : "";

    const yearFilter = filterByYear
      ? `&filters[tripDate][$gte]=${startYear}-01-01&filters[tripDate][$lte]=${endYear}-12-31`
      : "";

    const string =
      "/api/cards?populate=*&sort=tripDate" +
      filtrationString +
      `&pagination[page]=${page}&pagination[pageSize]=${pageSize}` +
      yearFilter;

    const res = await api.get(string);
    return res.data.data || [];
  };

  const fetchTripById = async (tripIdToFetch) => {
    try {
      const res = await api.get(`/api/cards/${tripIdToFetch}?populate=*`);
      return res.data.data || null;
    } catch (error) {
      console.error("Failed to fetch requested trip", error);
      return null;
    }
  };

  const readCards = async () => {
    const data = await fetchTrips({
      region: chosenRegion,
      page: refPage.current,
      pageSize,
      filterByYear,
      filterByRegion,
      startYear,
      endYear,
    });

    const nextHasMore = data.length === pageSize;
    setHasMore(nextHasMore);

    if (refPage.current === 1) {
      setTrips(data);

      const fallbackTrip = data.length > 0 ? data[0] : null;
      const selectedTrip = selectInitialTrip({
        tripIdParam,
        trips: data,
        defaultTrip: fallbackTrip,
      });

      if (tripIdParam) {
        if (selectedTrip) {
          setTripId(selectedTrip.id);
          setCurrentTrip(selectedTrip);
        } else {
          const requestedTrip = await fetchTripById(tripIdParam);
          if (requestedTrip) {
            setTrips((prevTrips) =>
              prevTrips.some(
                (trip) => String(trip.id) === String(requestedTrip.id),
              )
                ? prevTrips
                : [requestedTrip, ...prevTrips],
            );
            setTripId(requestedTrip.id);
            setCurrentTrip(requestedTrip);
          } else if (fallbackTrip) {
            setTripId(fallbackTrip.id);
            setCurrentTrip(fallbackTrip);
          } else {
            setTripId(null);
            setCurrentTrip(null);
          }
        }
      } else if (fallbackTrip) {
        setTripId(fallbackTrip.id);
        setCurrentTrip(fallbackTrip);
      }
    } else {
      setTrips((prevItems) => [...prevItems, ...data]);
    }

    setIsLoading(false);
  };

  const handleApplyFilters = async ({
    chosenRegion: region,
    startYear: newStartYear,
    endYear: newEndYear,
    filterByYear: newFilterByYear,
    filterByRegion: newFilterByRegion,
    showManyTracks: newShowManyTracks,
    highPagination: newHighPagination,
  }) => {
    setFilterDialogMessage("");

    try {
      const data = await fetchTrips({
        region,
        page: 1,
        pageSize,
        filterByYear: newFilterByYear,
        filterByRegion: newFilterByRegion,
        startYear: newStartYear,
        endYear: newEndYear,
      });

      if (data.length === 0) {
        setFilterDialogMessage("No results were found. Change selection.");
        setIsLoading(false);
        return false;
      }

      skipNextRead.current = true;
      refPage.current = 1;
      setTrips(data);
      setTripId(data[0].id);
      setChosenRegion(region);
      setStartYear(newStartYear);
      setEndYear(newEndYear);
      setFilterByYear(newFilterByYear);
      setFilterByRegion(newFilterByRegion);
      setShowManyTracks(newShowManyTracks);
      setHighPagination(newHighPagination);
      setIsLoading(false);
      setFilterDialogMessage("");
      return true;
    } catch (err) {
      setError(err);
      setFilterDialogMessage("Unable to check filters. Try again.");
      setIsLoading(false);
      return false;
    }
  };

  const handleScroll = (event) => {
    if (isLoading || !hasMore) return;

    const scrollTop = event.currentTarget.scrollTop;
    const clientHeight = event.currentTarget.clientHeight;
    const scrollHeight = event.currentTarget.scrollHeight;

    if (scrollTop + clientHeight < 0.95 * scrollHeight) return;

    setIsLoading(true);
    refPage.current += 1;
    readCards();
  };

  const noResultsMessage =
    !isLoading && !error && trips.length === 0
      ? "No tracks match the selected filters."
      : "";

  const currentTripSafe = currentTrip || {
    attributes: {
      gpxtrack: { data: [{ attributes: { url: "" } }] },
      gallery: { data: [] },
      tripInfo: "",
      cardTitle: "",
      difficulty: "",
      distance: "",
      heightGain: "",
      startPoint: "",
      trackLink: "",
      tripDate: "",
    },
  };

  const polygon =
    mapId === 0
      ? initPolygon
      : trips.find((card) => card.id === mapId)?.attributes.polygon ||
        initPolygon;

  // console.log("polygon", polygon);
  console.log(trips);
  // console.log(mapId);

  // console.log(trips[0].attributes.gpxtrack.data[0].attributes.url);

  const gpxFileUrl =
    API_URL + currentTripSafe.attributes.gpxtrack.data[0].attributes.url;
  const { tripInfo, cardTitle, difficulty, distance, heightGain, startPoint } =
    currentTripSafe.attributes;
  const tripGallery = currentTripSafe.attributes.gallery.data;
  // console.log(tripGallery[0].attributes.name);

  tripGallery.sort((a, b) => {
    const nameA = a.attributes.name;
    const nameB = b.attributes.name;
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }

    // names must be equal
    return 0;
  });

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    setTouchInfo((prevState) => ({
      ...prevState,
      startX: touch.clientX,
      startY: touch.clientY,
    }));
  };

  const handleTouchMove = (e) => {
    const touch = e.touches[0];
    setTouchInfo((prevState) => ({
      ...prevState,
      endX: touch.clientX,
      endY: touch.clientY,
    }));
  };

  const handleTouchEnd = () => {
    const diff = touchInfo.endX - touchInfo.startX;
    console.log("Touch diff:", diff);
    if (diff < -50) {
      nextPhoto();
    }
    if (diff > 50) {
      if (photoId > 0) {
        prevPhoto();
      }
    }
  };

  const nextPhoto = () => {
    if (photoId < tripGallery.length - 1) {
      setPhotoId(photoId + 1);
      setImgLoaded(true);
    }
  };

  const prevPhoto = () => {
    if (photoId > 0) {
      setPhotoId(photoId - 1);
      setImgLoaded(false);
    }
  };

  return (
    <div className="responsiveOuterBox">
      <div
        className="responsiveCardBox"
        id="box"
        ref={ref}
        onScroll={handleScroll}
      >
        {error ? (
          <Typography variant="h6" color="error" sx={{ p: 2 }}>
            Error loading tracks.
          </Typography>
        ) : noResultsMessage ? (
          <Typography variant="h6" color="white" sx={{ p: 2 }}>
            {noResultsMessage}
          </Typography>
        ) : (
          <>
            {trips.map((item) => {
              const attributes = item.attributes;

              return (
                <div key={item.id}>
                  <CardModuleMulti
                    cardTitle={attributes.cardTitle}
                    tripDate={attributes.tripDate}
                    cardCover={attributes.cardCover}
                    difficulty={attributes.difficulty}
                    distance={attributes.distance}
                    heightGain={attributes.heightGain}
                    trackLink={attributes.trackLink}
                    imageUrl={
                      API_URL +
                      attributes.cardCover.data.attributes.formats.medium.url
                    }
                    setCardId={setTripId}
                    cardId={item.id}
                    setPhotoId={setPhotoId}
                    setDisplayOption={setDisplayOption}
                    displayOption={displayOption}
                    setMapId={setMapId}
                    tripInfo={attributes.tripInfo}
                    startPoint={attributes.startPoint}
                  />
                </div>
              );
            })}
            {isLoading && trips.length > 0 && hasMore && (
              <Typography variant="body2" color="white" sx={{ p: 2 }}>
                Loading more tracks...
              </Typography>
            )}
          </>
        )}
      </div>

      <div
        className="responsiveSideBox"
        // style={{
        //   display: "flex",
        //   // flexwrap: "wrap",
        //   flexDirection: "column",
        //   // backgroundColor: "#1976d2",
        //   width: "80%",
        //   justifyContent: "space-around",
        //   overflow: "hidden",
        //   marginRight: "30px",
        //   marginLeft: "30px",
        //   flexGrow: "1",
        // }}
      >
        <div
          style={{
            minHeight: "40px",
          }}
        >
          <Typography variant="h5" color="white" sx={{ mt: 1 }}>
            {noResultsMessage || cardTitle}
            {/* {highPagination.toString()} */}
            {/* {pageSize.toString()} */}
          </Typography>
        </div>

        <div
          className="mapBox"
          style={{
            display:
              displayOption === "map" && trips.length > 0 ? "flex" : "none",
            flexGrow: "1",
            backgroundColor: "red",
            flexDirection: "row",
            justifyContent: "center",
          }}
        >
          <MapModuleForMaps
            chosenRegion={chosenRegion}
            polygon={polygon}
            cardItemsList={trips}
            displayOption={displayOption}
            setTripId={setTripId}
            setMapId={setMapId}
            showManyTracks={showManyTracks}
            filterByYear={filterByYear}
            filterByRegion={filterByRegion}
          />
        </div>

        <div
          style={{
            display:
              displayOption === "gallery" && trips.length > 0 ? "flex" : "none",
            flexDirection: "column",
            flexGrow: "1",
          }}
        >
          <div
            className="newGalleryContainer"
            ref={divRef}
            onClick={handleTap}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{
              backgroundImage:
                tripGallery.length > 0
                  ? 'url("' +
                    API_URL +
                    tripGallery[photoId].attributes.formats.xlarge.url +
                    '")'
                  : "none",
              flexGrow: "1",
            }}
          ></div>

          {imgLoaded &&
            tripGallery.length > 0 &&
            photoId < tripGallery.length - 1 && (
              <img
                className="preload-img"
                src={
                  API_URL +
                  tripGallery[photoId + 1].attributes.formats.xlarge.url
                }
                alt="Next trip"
              ></img>
            )}
          <div
            className="arrowsBox"
            style={{
              display: "flex",
              flexwrap: "wrap",
              flexDirection: "row",
              justifyContent: "space-around",
              marginTop: "2px",
            }}
          >
            <Button
              size="large"
              variant="outlined"
              startIcon={<ArrowBackIosIcon />}
              sx={{ color: "white" }}
              onClick={() => {
                prevPhoto();
              }}
              disabled={photoId < 1}
            >
              BACK
            </Button>

            <Button
              size="large"
              variant="outlined"
              // endIcon={<ArrowForwardIosIcon />}
              sx={{
                color: "gray",
                // borderColor: "white",
              }}
            >
              {photoId + 1} / {tripGallery.length}
            </Button>

            <Button
              size="large"
              variant="outlined"
              endIcon={<ArrowForwardIosIcon />}
              sx={{
                color: "white",
                // borderColor: "white",
              }}
              onClick={() => {
                nextPhoto();
              }}
              disabled={photoId >= tripGallery.length - 1}
            >
              NEXT
            </Button>
          </div>
        </div>

        {trips.length > 0 && (
          <Buttons
            setDisplayOption={setDisplayOption}
            setOpenGpxDialog={setOpenGpxDialog}
            setOpenInfoDialog={setOpenInfoDialog}
            setOpenFilterDialog={setOpenFilterDialog}
            setChosenRegion={setChosenRegion}
            chosenRegion={chosenRegion}
            cardId={tripId}
            setMapId={setMapId}
          />
        )}

        {openGpxDialog && (
          <CustomizedDialogs
            openGpxDialog={openGpxDialog}
            setOpenGpxDialog={setOpenGpxDialog}
            gpxFileUrl={gpxFileUrl}
            cardTitle={cardTitle}
          />
        )}

        {openInfoDialog && (
          <CustomizedDialogsInfo
            openInfoDialog={openInfoDialog}
            setOpenInfoDialog={setOpenInfoDialog}
            tripInfo={tripInfo}
            cardTitle={cardTitle}
            difficulty={difficulty}
            distance={distance}
            heightGain={heightGain}
            startPoint={startPoint}
            region={currentTrip.attributes.trackLink}
            tripDate={currentTrip.attributes.tripDate}
          />
        )}

        {openFilterDialog && (
          <CustomizedDialogsFilter
            openFilterDialog={openFilterDialog}
            setOpenFilterDialog={setOpenFilterDialog}
            filterMessage={filterDialogMessage}
            applyFilters={handleApplyFilters}
            chosenRegion={chosenRegion}
            showManyTracks={showManyTracks}
            highPagination={highPagination}
            startYear={startYear}
            endYear={endYear}
            filterByYear={filterByYear}
            filterByRegion={filterByRegion}
          />
        )}
      </div>
    </div>
  );
}
